import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTheme } from '@tanium/react-theme-context';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import KubernetesClusterSelectors from 'components/pages/console/store/KubernetesClusterSelectors';
import KubernetesClusterSlice from 'components/pages/console/store/KubernetesClusterSlice';
import IDropdownOption from 'common/IDropdownOption';
import {
  TARGET_ID,
  TARGET_NAME,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import { isProcessing } from 'store/ActionState';

export const KubernetesClusterEnvironment = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();
  const kubernetesClusters = useSelector(KubernetesClusterSelectors.selectAll);
  const selectFetchClustersState = useSelector(KubernetesClusterSelectors.selectFetchClustersState);
  const [kubernetesClusterOptions, setKubernetesClusterOptions] = useState<IDropdownOption[]>([]);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  const [targetNameQuery] = useQueryParam(TARGET_NAME, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);

  useEffect(() => {
    dispatch(KubernetesClusterSlice.actions.fetchClusters());
  }, [dispatch]);

  useEffect(() => {
    if (kubernetesClusters && kubernetesClusters.length) {
      setKubernetesClusterOptions(
        kubernetesClusters.map((s) => ({ label: s.name, value: s.id, description: s.description }))
      );
    }
  }, [setKubernetesClusterOptions, kubernetesClusters]);

  useEffect(() => {
    if (targetNameQuery && kubernetesClusters?.length) {
      // Check for both cluster ID using cluster Name
      const selectedClusterIndex = kubernetesClusters.findIndex((k) => k.name === targetNameQuery);
      if (selectedClusterIndex >= 0) {
        const targetId = kubernetesClusters[selectedClusterIndex].id;
        setTargetIdQuery(targetId);
        void formik.setFieldValue(TARGET_ID, targetId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kubernetesClusters, targetIdQuery, targetNameQuery]);

  // Ran on initialization
  useEffect(() => {
    if (targetIdQuery && kubernetesClusters?.length) {
      void formik.setFieldValue(TARGET_ID, targetIdQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kubernetesClusters, targetIdQuery]);

  const formInputIdPrefix = 'create-request-';
  return (
    <FormSection title={t('SELECT_CLUSTER')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('KUBERNETES_CLUSTER')}
            required
            description={t('KUBERNETES_CLUSTER_INFO')}
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {<BarsTransition isLoading={isProcessing(selectFetchClustersState)} />}
            {!isProcessing(selectFetchClustersState) && kubernetesClusters && (
              <FormSelectInput
                placeholder={t('K8S_ENVIRONMENT_PLACEHOLDER')}
                onChange={(option): void => {
                  void formik.setFieldValue(TARGET_ID, option.value);
                  void formik.setFieldValue(TARGET_NAME, option.label);
                  void formik.setFieldValue(ACCESS_TYPE, '');
                  void formik.setFieldValue(ACCESS_TYPES, []);
                  setTargetIdQuery(option.value);
                  setAccessTypeQuery(undefined);
                }}
                value={kubernetesClusterOptions.find((option) => {
                  return option.value === field.value;
                })}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={kubernetesClusterOptions}
                aria-label={t('KUBERNETES_CLUSTER')}
                formatOptionLabel={(
                  option: {
                    value: string;
                    label: string;
                    description: string;
                  },
                  state
                ) => {
                  const isSelected =
                    state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                  /* istanbul ignore next */
                  const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                  return (
                    <Stack direction="column" itemSpacing={0.5}>
                      <Box display="inline-flex">
                        <Box mr={1}>{option.label}</Box>
                        <Box color={subtextColor} fontSize={11}>
                          {option.value}
                        </Box>
                      </Box>
                      <Box color={subtextColor} fontSize={11}>
                        {option.description}
                      </Box>
                    </Stack>
                  );
                }}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    marginTop: theme.space[1],
                    marginBottom: theme.space[1],
                    position: 'relative',
                    transform: 'none',
                    whiteSpace: 'normal',
                    maxHeight: '3.75em',
                  }),
                  input: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: theme.space[1] - 2,
                    paddingTop: 0,
                  }),
                }}
              />
            )}
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default KubernetesClusterEnvironment;
